// Financial Freedom Calculator -- API fetcher for the PUBLIC orchestrator
// endpoint (POST .../calculate).
//
// AUTH / BASE-URL NOTE: this is an UNAUTHENTICATED marketing endpoint, so we
// deliberately do NOT go through the shared `apiClient` (which attaches a
// Bearer token and, on a 401, silently refreshes / bounces the visitor to the
// WordPress login page -- both wrong for an anonymous frontpage tool). Instead
// we reuse apiClient's already-resolved baseURL (so it correctly targets
// forge-api.ki-wealth.com/api/v1 in production and the same-origin Vite proxy in
// dev) via a clean, interceptor-free axios instance -- exactly the pattern the
// sibling etf-wealth-builder-calculator uses.

import axios from 'axios';

import { apiClient } from '../../../../api/client';
import type {
    FinancialFreedomInputs,
    FinancialFreedomOutputs,
} from '../types';

// Reuse the resolved base URL (`/api/v1` in dev, `https://host/api/v1` in prod)
// but with NONE of apiClient's interceptors. Timeout is generous: the very
// first request after a cold backend boot pays a one-off Numba JIT compile.
const publicApiClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 20_000,
    headers: { 'Content-Type': 'application/json' },
});

// Matches the confirmed backend route: the feature-slice router prefix
// (`/free-calculators/financial-freedom-calculator`) is appended to the
// baseURL's `/api/v1` segment.
const CALCULATE_ENDPOINT =
    '/free-calculators/financial-freedom-calculator/calculate';

export const calculateFinancialFreedom = async (
    inputs: FinancialFreedomInputs,
): Promise<FinancialFreedomOutputs> => {
    try {
        const { data } = await publicApiClient.post<FinancialFreedomOutputs>(
            CALCULATE_ENDPOINT,
            inputs,
        );
        return data;
    } catch (error) {
        // Surface the backend's structured `{ detail }` (e.g. a 422 bounds
        // error) as a clean Error message the UI can render, falling back to a
        // generic message otherwise.
        if (axios.isAxiosError(error)) {
            const detail = (error.response?.data as { detail?: string } | undefined)
                ?.detail;
            throw new Error(
                detail || 'Failed to calculate financial freedom. Please try again.',
            );
        }
        throw error instanceof Error
            ? error
            : new Error('Failed to calculate financial freedom. Please try again.');
    }
};
